// Resolve a Form#theme JSONB payload (all keys optional) into a set of CSS
// custom properties applied inline on the page root. Kept intentionally
// separate from the dashboard's brand-color helpers: this page renders a
// single tenant's form theme, not the app's own installation brand.
export const DEFAULT_THEME = {
  primary_color: '#146B57',
  button_color: '#146B57',
  button_text_color: '#FFFFFF',
  background_color: '#F5F4EF',
  card_color: '#FFFFFF',
  text_color: '#1B1B18',
  choice_text_color: '',
  logo_url: '',
  header_image_url: '',
  // Altura máxima (px) da imagem de topo/capa. Muitos clientes usam o próprio
  // logo aqui, exibido com object-fit:contain (mantém proporção); esse valor
  // controla o tamanho via slider na aba Aparência. Aceita número (px) ou os
  // rótulos antigos (small/medium/large) por compatibilidade.
  header_size: 72,
  radius: 16,
  font: 'sans',
};

const HEADER_MAX_HEIGHTS = { small: 96, medium: 150, large: 240 };
const HEADER_MIN_PX = 32;
const HEADER_MAX_PX = 260;

const resolveHeaderMaxHeight = value => {
  const numeric = typeof value === 'number' ? value : HEADER_MAX_HEIGHTS[value];
  if (Number.isFinite(numeric)) {
    return `${Math.min(Math.max(numeric, HEADER_MIN_PX), HEADER_MAX_PX)}px`;
  }
  return `${DEFAULT_THEME.header_size}px`;
};

const HEX_COLOR_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

const isValidHex = value =>
  typeof value === 'string' && HEX_COLOR_RE.test(value.trim());

const resolveColor = (value, fallback) =>
  isValidHex(value) ? value.trim() : fallback;

const resolveRadius = value => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : DEFAULT_THEME.radius;
};

const FONT_STACKS = {
  sans: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  serif:
    "'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, 'Times New Roman', serif",
};

export const buildThemeStyle = (theme = {}) => {
  const merged = { ...DEFAULT_THEME, ...(theme || {}) };
  const radius = resolveRadius(merged.radius);

  return {
    '--csf-primary': resolveColor(
      merged.primary_color,
      DEFAULT_THEME.primary_color
    ),
    '--csf-button': resolveColor(
      merged.button_color,
      DEFAULT_THEME.button_color
    ),
    '--csf-button-text': resolveColor(
      merged.button_text_color,
      DEFAULT_THEME.button_text_color
    ),
    '--csf-bg': resolveColor(
      merged.background_color,
      DEFAULT_THEME.background_color
    ),
    '--csf-card-bg': resolveColor(merged.card_color, DEFAULT_THEME.card_color),
    '--csf-text': resolveColor(merged.text_color, DEFAULT_THEME.text_color),
    '--csf-choice-text': resolveColor(
      merged.choice_text_color,
      resolveColor(merged.text_color, DEFAULT_THEME.text_color)
    ),
    '--csf-radius': `${radius}px`,
    '--csf-radius-lg': `${Math.max(radius * 1.375, radius + 4)}px`,
    '--csf-header-max-height': resolveHeaderMaxHeight(merged.header_size),
    '--csf-font': FONT_STACKS[merged.font] || FONT_STACKS.sans,
  };
};

export const resolveLogoUrl = theme => (theme && theme.logo_url) || '';
export const resolveHeaderImageUrl = theme =>
  (theme && theme.header_image_url) || '';
